import React from 'react';
import {
  Box, Checkbox, FormControlLabel, Grid, Paper, Stack, TextField, Typography, Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ID_CARD_CATEGORIES,
  ID_CARD_ITEM_TYPES,
  calculateIdCardConfigTotal,
  calculateIdCardLineTotal,
  formatIdCardCurrency,
  getIdCardItemLabel,
} from 'Containers/Billing/idCardServiceConfig';

function IdCardServiceConfigPanel({ config, onChange, disabled = false, compact = false }) {
  const updateConfig = (updater) => {
    if (disabled || typeof onChange !== 'function') return;
    onChange(updater(config));
  };

  const toggleCategory = (categoryKey, enabled) => {
    updateConfig((prev) => ({
      ...prev,
      categories: {
        ...prev.categories,
        [categoryKey]: {
          ...prev.categories[categoryKey],
          enabled,
        },
      },
    }));
  };

  const updateItem = (categoryKey, itemKey, patch) => {
    updateConfig((prev) => ({
      ...prev,
      categories: {
        ...prev.categories,
        [categoryKey]: {
          ...prev.categories[categoryKey],
          items: {
            ...prev.categories[categoryKey].items,
            [itemKey]: {
              ...prev.categories[categoryKey].items[itemKey],
              ...patch,
            },
          },
        },
      },
    }));
  };

  const grandTotal = calculateIdCardConfigTotal(config);

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        ID card configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select Staff and/or Student ID card. Then choose Full ID Card Service and/or individual items, and enter count with per unit price.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        {ID_CARD_CATEGORIES.map(({ key, label }) => (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                checked={Boolean(config?.categories?.[key]?.enabled)}
                onChange={(e) => toggleCategory(key, e.target.checked)}
                disabled={disabled}
              />
            }
            label={label}
          />
        ))}
      </Stack>

      {ID_CARD_CATEGORIES.map(({ key: categoryKey, label: categoryLabel }) => {
        const category = config?.categories?.[categoryKey];
        if (!category?.enabled) return null;

        const categoryTotal = ID_CARD_ITEM_TYPES.reduce(
          (sum, { key: itemKey }) => sum + calculateIdCardLineTotal(category.items?.[itemKey]),
          0,
        );

        return (
          <Paper
            key={categoryKey}
            variant="outlined"
            sx={{
              p: compact ? 1.5 : 2,
              mb: 2,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom>
              {categoryLabel}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={1.5}>
              {ID_CARD_ITEM_TYPES.map((itemType) => {
                const itemKey = itemType.key;
                const itemLabel = getIdCardItemLabel(itemType, categoryKey);
                const item = category.items?.[itemKey] || { selected: false, count: '', unitPrice: '' };
                const lineTotal = calculateIdCardLineTotal(item);

                return (
                  <Box key={itemKey}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(item.selected)}
                          onChange={(e) => updateItem(categoryKey, itemKey, {
                            selected: e.target.checked,
                            ...(e.target.checked ? {} : { count: '', unitPrice: '' }),
                          })}
                          disabled={disabled}
                          size="small"
                        />
                      }
                      label={
                        <Typography fontWeight={itemKey === 'full_service' ? 700 : 400}>
                          {itemLabel}
                        </Typography>
                      }
                    />
                    {item.selected ? (
                      <Grid container spacing={1.5} sx={{ pl: 4, mt: 0.5 }}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Count"
                            type="number"
                            size="small"
                            fullWidth
                            value={item.count}
                            onChange={(e) => updateItem(categoryKey, itemKey, { count: e.target.value })}
                            disabled={disabled}
                            inputProps={{ min: 0, step: 1 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Per unit price (₹)"
                            type="number"
                            size="small"
                            fullWidth
                            value={item.unitPrice}
                            onChange={(e) => updateItem(categoryKey, itemKey, { unitPrice: e.target.value })}
                            disabled={disabled}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4} display="flex" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Line total:{' '}
                            <Box component="span" fontWeight={700} color="text.primary">
                              {formatIdCardCurrency(lineTotal)}
                            </Box>
                          </Typography>
                        </Grid>
                      </Grid>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>

            <Typography variant="body2" fontWeight={600} sx={{ mt: 2, textAlign: 'right' }}>
              {categoryLabel} subtotal: {formatIdCardCurrency(categoryTotal)}
            </Typography>
          </Paper>
        );
      })}

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
          borderColor: 'success.light',
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} align="right">
          Grand total: {formatIdCardCurrency(grandTotal)}
        </Typography>
      </Paper>
    </Box>
  );
}

export default IdCardServiceConfigPanel;
